import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// ==================== ICONS ====================
const BookOpenIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
  </svg>
);

const QrCodeIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5zM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5zM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0113.5 9.375v-4.5z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 6.75h.75v.75h-.75v-.75zM6.75 16.5h.75v.75h-.75v-.75zM16.5 6.75h.75v.75h-.75v-.75zM13.5 13.5h.75v.75h-.75v-.75zM13.5 19.5h.75v.75h-.75v-.75zM19.5 13.5h.75v.75h-.75v-.75zM19.5 19.5h.75v.75h-.75v-.75zM16.5 16.5h.75v.75h-.75v-.75z" />
  </svg>
);

const SparklesIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
  </svg>
);

const DownloadIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

// ==================== REUSABLE COMPONENTS ====================

const Container = ({ className = "", children }) => (
  <div className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>
);

const SectionHeader = ({ eyebrow, title, description, align = "left" }) => (
  <div className={align === "center" ? "text-center max-w-3xl mx-auto" : "max-w-3xl"}>
    {eyebrow && (
      <div className={align === "center" ? "flex justify-center" : ""}>
        <span className="inline-flex items-center gap-2 text-school-goldDark font-semibold tracking-[0.18em] uppercase text-xs">
          <span className="h-1.5 w-1.5 rounded-full bg-school-gold" aria-hidden="true" />
          {eyebrow}
        </span>
      </div>
    )}
    <h2 className="mt-3 text-3xl sm:text-4xl md:text-5xl font-heading font-bold text-school-blue leading-[1.08] tracking-tight">
      {title}
    </h2>
    {description && (
      <p className={`mt-4 text-base md:text-lg text-gray-600 leading-7 ${align === "center" ? "mx-auto" : ""}`}>
        {description}
      </p>
    )}
  </div>
);

const FeatureCard = ({ icon, title, description }) => (
  <div className="group flex flex-col p-8 bg-white rounded-3xl border border-gray-100 shadow-soft hover:shadow-md transition-all duration-300 hover:-translate-y-0.5">
    <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-school-blue/8 text-school-blue group-hover:bg-school-blue group-hover:text-white transition-colors duration-300 mb-6 shadow-sm">
      {icon}
    </div>
    <h3 className="text-lg md:text-xl font-heading font-bold text-gray-900 mb-2 tracking-tight group-hover:text-school-gold transition-colors">
      {title}
    </h3>
    <p className="text-sm md:text-base text-gray-600 leading-7">{description}</p>
  </div>
);

// ==================== MAIN LTG PHASE 1 COMPONENT ====================

const LTGPhase1 = () => {
  return (
    <div className="min-h-screen font-sans text-gray-600 antialiased selection:bg-school-gold/30 selection:text-school-blue" style={{ backgroundColor: '#F5EFE0' }}>
      <Navbar />

      <main id="main-content">

        {/* ==================== HERO SECTION ==================== */}
        <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center overflow-hidden bg-[#0F2240]">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.35),transparent_55%)]" aria-hidden="true" />
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 100%, rgba(197,145,50,0.1) 0%, transparent 70%)' }} />

          <Container className="relative z-10 pt-32 pb-16 md:pt-40 md:pb-20">
            <div className="max-w-4xl bg-[#0F2240]">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white/90 text-xs sm:text-sm font-semibold mb-7">
                <span className="w-2 h-2 rounded-full bg-green-400" aria-hidden="true" />
                LTG Phase 1
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl font-heading font-bold text-white tracking-tight leading-[1.05]">
                LTG at P.S. 85 Phase 1
              </h1>

              <p className="mt-6 text-lg md:text-xl text-gray-200 leading-relaxed max-w-3xl">
                Interactive Rainforest Experience & Digital Learning
              </p>
            </div>
          </Container>
        </section>

        {/* ==================== MAIN CONTENT ==================== */}
        <section className="py-20 md:py-28 bg-white">
          <Container>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">

              {/* Image */}
              <div className="order-2 lg:order-1">
                <div className="relative">
                  <div className="absolute -inset-4 bg-brand-50 rounded-[2.25rem] -z-10 transform translate-x-4 translate-y-4" aria-hidden="true" />
                  <img
                    src="https://3.files.edl.io/8dfa/22/05/06/115454-4d142aef-f10e-43b7-b63e-2f3a6406b71d.jpg"
                    alt="Interactive Rainforest Experience"
                    className="rounded-[2.25rem] shadow-soft w-full border border-gray-100"
                    loading="lazy"
                  />
                </div>
              </div>

              {/* Content */}
              <div className="order-1 lg:order-2">
                <SectionHeader
                  eyebrow="Phase 1"
                  title="Interactive Rainforest Experience"
                  description="Bringing learning to life through technology and creativity"
                />
                <div className="mt-8 space-y-6 text-base md:text-lg text-gray-600 leading-relaxed">
                  <p>
                    We showcased our learning with an Interactive Rainforest Experience where students shared their performances and recordings of their books with QR codes. Our scholars in the Saturday Academy engaged in animal research projects and published their books in Book Creator.
                  </p>
                  <p>
                    Our teachers and students also worked in partnership with Digital Age Learning to show what they learned through movement.
                  </p>
                </div>
              </div>
            </div>
          </Container>
        </section>

        {/* ==================== FEATURES GRID ==================== */}
        <section className="py-20 md:py-28 bg-[#EDE5D0]">
          <Container>
            <SectionHeader
              align="center"
              eyebrow="Key Features"
              title="What Students Created"
              description="Explore the innovative ways our students showcased their learning"
            />

            <div className="mt-14 grid md:grid-cols-3 gap-6 lg:gap-8">
              <FeatureCard
                icon={<BookOpenIcon />}
                title="Digital Publishing"
                description="Students created and published their own books using Book Creator, bringing their research to life."
              />

              <FeatureCard
                icon={<QrCodeIcon />}
                title="QR Code Integration"
                description="Interactive QR codes allowed visitors to access student performances and recordings."
              />

              <FeatureCard
                icon={<SparklesIcon />}
                title="Movement & Learning"
                description="Partnership with Digital Age Learning incorporated movement to demonstrate understanding."
              />
            </div>
          </Container>
        </section>

        {/* ==================== DOWNLOAD RESOURCE ==================== */}
        <section className="py-20 md:py-28 bg-white">
          <Container>
            <div className="rounded-3xl border border-school-gold/20 p-8 md:p-12 shadow-soft" style={{ backgroundColor: '#EDE5D0' }}>
              <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-2xl md:text-3xl font-heading font-bold text-gray-900 mb-3 tracking-tight">
                    Curriculum Resources
                  </h3>
                  <p className="text-base md:text-lg text-gray-600 leading-7">
                    Download our Digital Age LTG Curriculum Map to learn more about this program.
                  </p>
                </div>
                <a
                  href="https://4.files.edl.io/58ea/05/05/22/112323-53091eeb-ec89-4497-b97f-403df1ea5b13.docx"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-school-blue text-white font-bold hover:bg-[#0F2240] transition-colors shadow-soft whitespace-nowrap"
                  download
                >
                  <DownloadIcon />
                  Download Curriculum Map
                </a>
              </div>
            </div>
          </Container>
        </section>

        {/* ==================== CTA SECTION ==================== */}
        <section className="relative py-24 md:py-32 bg-school-gold overflow-hidden">
          <div className="absolute -right-20 -top-20 w-96 h-96 bg-school-blue/8 rounded-full blur-3xl pointer-events-none" />

          <Container className="relative text-center">
            <h2 className="text-4xl md:text-6xl font-heading font-bold text-school-blue mb-6">
              Explore More LTG Phases
            </h2>
            <p className="text-xl text-school-blue max-w-2xl mx-auto mb-10 font-light">
              Discover how our Learning Technology Grant continues to evolve across different phases.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/ltg-phase-2"
                className="px-8 py-4 rounded-full bg-school-blue text-white font-bold hover:bg-[#0F2240] transition-colors shadow-soft"
              >
                View Phase 2
              </a>
              <a
                href="/nysed-grant-2025"
                className="px-8 py-4 rounded-full bg-white/30 text-school-blue border-2 border-school-blue/20 font-medium hover:bg-white/50 transition-colors"
              >
                Back to Grant Overview
              </a>
            </div>
          </Container>
        </section>

        <Footer />
      </main>
    </div>
  );
};

export default LTGPhase1;
